import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, timeout } from 'rxjs';
import { PlatformReportsService } from '../../services/platform-reports/platform-reports.service';
import { ToastService } from '../../services/toast/toast.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { CsvColumn, downloadCsv } from '../../common-files/csv';

type ReportKey = 'establishments' | 'utsavs' | 'access' | 'activity' | 'frontdesk' | 'funnel' | 'roleDrift' | 'media';

// platform-wide reports: every establishment at once (or one, via the filter).
// Same pattern as the branch Reports screen: one API call per tab.
@Component({
  selector: 'app-platform-reports',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './platform-reports.component.html',
  styleUrls: ['./platform-reports.component.scss', '../../../common-style/report-page.scss'],
})
export class PlatformReportsComponent implements OnInit {
  readonly reportTabs: Array<{ key: ReportKey; label: string }> = [
    { key: 'establishments', label: 'Establishments' },
    { key: 'utsavs', label: 'Utsavs' },
    { key: 'access', label: 'Users & access' },
    { key: 'activity', label: 'Activity log' },
    { key: 'frontdesk', label: 'Front-desk activity' },
    { key: 'funnel', label: 'Bookings & cancellations' },
    { key: 'roleDrift', label: 'Role drift' },
    { key: 'media', label: 'Media' },
  ];
  activeReport: ReportKey = 'establishments';

  // filters (kept while switching tabs)
  establishment = new FormControl<number | ''>('');
  activeOnly = new FormControl<string>('true');
  establishments: any[] = [];
  // date range (activity, front-desk): default last 7 days, IST
  fromDate = new FormControl<string>('');
  toDate = new FormControl<string>('');
  // activity-only filters
  actUser = new FormControl<string>('');
  actModule = new FormControl<string>('');
  actMethod = new FormControl<string>('');
  actFailures = new FormControl<string>('');
  actPage: number = 1;
  // funnel: utsav within the chosen establishment
  utsav = new FormControl<number | ''>('');
  utsavs: any[] = [];

  report: any = null;
  isLoading: boolean = false;
  reportLoadFailed: boolean = false;
  expandedUser: number | null = null;

  constructor(
    private platformReportsService: PlatformReportsService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    // the establishments the signed-in user can act on come with the sign-in response
    try {
      const stored = JSON.parse(localStorage.getItem('establishments') || '[]');
      this.establishments = Array.isArray(stored) ? stored : [];
    } catch {
      this.establishments = [];
    }
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const weekAgo = new Date(Date.parse(today) - 6 * 86400000).toISOString().slice(0, 10);
    this.fromDate.setValue(weekAgo);
    this.toDate.setValue(today);
    this.loadReport();
  }

  get usesDateRange(): boolean {
    return this.activeReport === 'activity' || this.activeReport === 'frontdesk';
  }

  // the funnel can narrow to one utsav once an establishment is chosen
  onEstablishmentChange(): void {
    this.utsav.setValue('');
    this.utsavs = [];
    const establishmentId = this.establishment.value ? Number(this.establishment.value) : null;
    if (establishmentId && this.activeReport === 'funnel') {
      this.platformReportsService.getUtsavOverview(establishmentId, false).subscribe({
        next: (res: any) => (this.utsavs = res?.data?.rows || []),
        error: () => (this.utsavs = []),
      });
    }
    this.loadReport();
  }

  applyActivityFilters(event?: Event): void {
    event?.preventDefault();
    this.actPage = 1;
    this.loadReport();
  }

  goToPage(page: number): void {
    if (page < 1 || page === this.actPage) return;
    this.actPage = page;
    this.loadReport();
  }

  get activityPages(): number {
    return this.report?.total ? Math.ceil(this.report.total / (this.report.pageSize || 50)) : 1;
  }

  get activeReportLabel(): string {
    return this.reportTabs.find((t) => t.key === this.activeReport)?.label ?? '';
  }

  // the establishment filter applies to place-based tabs only
  get usesEstablishmentFilter(): boolean {
    return ['establishments', 'utsavs', 'frontdesk', 'funnel'].includes(this.activeReport);
  }

  // role drift: narrow to one role
  driftRole = new FormControl<number | ''>('');

  setReport(key: ReportKey): void {
    if (this.activeReport === key) return;
    this.activeReport = key;
    this.actPage = 1;
    if (key === 'funnel' && this.establishment.value && !this.utsavs.length) {
      this.onEstablishmentChange();
      return;
    }
    this.loadReport();
  }

  loadReport(): void {
    this.report = null;
    this.reportLoadFailed = false;
    this.expandedUser = null;
    this.isLoading = true;
    // never leave the page behind the loader: give up after 90 s with a clear message
    this.requestFor(this.activeReport).pipe(timeout(90000)).subscribe({
      next: (res: any) => {
        this.report = res?.data || null;
        this.isLoading = false;
      },
      error: (err) => {
        this.report = null;
        this.reportLoadFailed = true;
        this.isLoading = false;
        const timedOut = err?.name === 'TimeoutError';
        this.toastService.error(
          timedOut ? 'The report took too long to load. Please try again.' : err?.error?.message || 'Failed to load report.'
        );
      },
    });
  }

  private requestFor(key: ReportKey): Observable<any> {
    const establishmentId = this.establishment.value ? Number(this.establishment.value) : null;
    const from = this.fromDate.value || '';
    const to = this.toDate.value || '';
    switch (key) {
      case 'activity':
        return this.platformReportsService.getActivity({
          from,
          to,
          user: this.actUser.value?.trim() || undefined,
          module: this.actModule.value || undefined,
          method: this.actMethod.value || undefined,
          failuresOnly: this.actFailures.value === 'true' ? true : undefined,
          page: this.actPage,
        });
      case 'frontdesk':
        return this.platformReportsService.getFrontdesk(from, to, establishmentId);
      case 'funnel':
        return this.platformReportsService.getFunnel(establishmentId, this.utsav.value ? Number(this.utsav.value) : null);
      case 'roleDrift':
        return this.platformReportsService.getRoleDrift(this.driftRole.value ? Number(this.driftRole.value) : null);
      case 'media':
        return this.platformReportsService.getMediaInventory();
      case 'utsavs':
        return this.platformReportsService.getUtsavOverview(establishmentId, this.activeOnly.value === 'true');
      case 'access':
        return this.platformReportsService.getAccessAudit();
      case 'establishments':
      default:
        return this.platformReportsService.getEstablishmentOverview(establishmentId);
    }
  }

  // stable key for a place row (the central place has no id). Kept as a method:
  // Angular 18 miscompiles `??` inside a track expression (ReferenceError at runtime).
  placeKey(place: any): string {
    return place?.establishmentId == null ? 'central' : String(place.establishmentId);
  }

  // access audit: the roles × places matrix cell
  usersAt(role: any, place: any): number {
    const key = place.establishmentId == null ? 'central' : String(place.establishmentId);
    return role?.byPlace?.[key] ?? 0;
  }

  toggleUser(sumId: number): void {
    this.expandedUser = this.expandedUser === sumId ? null : sumId;
  }

  staffLabel(row: any): string {
    return (row.staffByRole || []).map((s: any) => `${s.roleName} ${s.users}`).join(' · ');
  }

  // -- CSV export: the main table of the active tab --
  exportCsv(): void {
    if (!this.report) return;
    const r = this.report;
    const stamp = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const out = (name: string, columns: CsvColumn[], rows: any[]) => downloadCsv(`platform-${name}-${stamp}`, columns, rows || []);
    switch (this.activeReport) {
      case 'establishments':
        return out('establishments', [
          { key: 'name', label: 'Establishment' }, { key: 'code', label: 'Code' }, { key: 'properties', label: 'Properties' },
          { key: 'rooms', label: 'Rooms' }, { key: 'capacity', label: 'Capacity' }, { key: 'activeUtsavs', label: 'Active utsavs' },
          { key: 'utsavs', label: 'Utsavs' }, { key: 'bookings', label: 'Live bookings' }, { key: 'guests', label: 'Guests' },
          { key: 'checkedIn', label: 'Checked-in' }, { key: 'occupiedBeds', label: 'Occupied beds' }, { key: 'occupancyPct', label: 'Occupancy %' },
          { key: 'staff', label: 'Staff' },
        ], r.rows);
      case 'utsavs':
        return out('utsavs', [
          { key: 'name', label: 'Utsav' }, { key: 'utsavCode', label: 'Code' }, { key: 'establishmentName', label: 'Establishment' },
          { key: 'isActive', label: 'Active' }, { key: 'utsavStartDate', label: 'Utsav start' }, { key: 'utsavEndDate', label: 'Utsav end' },
          { key: 'accommodationStartDate', label: 'Accommodation start' }, { key: 'accommodationEndDate', label: 'Accommodation end' },
          { key: 'targetCapacity', label: 'Target' }, { key: 'assigned.total', label: 'Assigned beds' }, { key: 'heldBeds', label: 'Held beds' },
          { key: 'heldPct', label: 'Held %' }, { key: 'bookings.pending', label: 'Pending' }, { key: 'bookings.assigned', label: 'Assigned bookings' },
          { key: 'bookings.confirmed', label: 'Confirmed' }, { key: 'bookings.checkedIn', label: 'Checked-in' }, { key: 'bookings.checkedOut', label: 'Checked-out' },
          { key: 'bookings.cancelled', label: 'Cancelled' }, { key: 'bookings.rejected', label: 'Rejected' }, { key: 'bookings.guests', label: 'Guests' },
        ], r.rows);
      case 'access': {
        const columns: CsvColumn[] = [{ key: 'roleName', label: 'Role' }, { key: 'applicableFor', label: 'Domain' }];
        for (const p of r.places || []) columns.push({ key: `byPlace.${p.establishmentId == null ? 'central' : p.establishmentId}`, label: p.name });
        columns.push({ key: 'total', label: 'Total' });
        return out('users-per-role', columns, r.roles);
      }
      case 'activity':
        return out('activity', [
          { key: 'at', label: 'When (IST)' }, { key: 'user', label: 'User' }, { key: 'method', label: 'Method' },
          { key: 'path', label: 'Path' }, { key: 'module', label: 'Module' }, { key: 'success', label: 'Success' }, { key: 'ms', label: 'ms' },
        ], r.rows);
      case 'frontdesk':
        return out('frontdesk', [
          { key: 'name', label: 'Establishment' }, { key: 'code', label: 'Code' }, { key: 'checkins', label: 'Check-ins' },
          { key: 'checkouts', label: 'Check-outs' }, { key: 'late', label: 'Late arrivals' }, { key: 'avgDelayDays', label: 'Avg delay (days)' },
        ], r.rows);
      case 'funnel':
        return out('bookings', [
          { key: 'name', label: 'Establishment' }, { key: 'code', label: 'Code' }, { key: 'total', label: 'Total' }, { key: 'pending', label: 'Pending' },
          { key: 'assigned', label: 'Assigned' }, { key: 'confirmed', label: 'Confirmed' }, { key: 'checkedIn', label: 'Checked-in' },
          { key: 'checkedOut', label: 'Checked-out' }, { key: 'cancelled', label: 'Cancelled' }, { key: 'rejected', label: 'Rejected' },
          { key: 'cancelPct', label: 'Drop %' }, { key: 'guests', label: 'Guests' }, { key: 'heldBedAnomalies', label: 'Held beds (dropped)' },
        ], r.rows);
      case 'roleDrift':
        return out('role-drift', [
          { key: 'fullName', label: 'User' }, { key: 'userName', label: 'Username' }, { key: 'roleName', label: 'Role' },
          { key: 'place', label: 'Place' }, { key: 'missing', label: 'Missing template pairs' }, { key: 'extra', label: 'Extra pairs' },
        ], r.assignments);
      case 'media':
        return out('media', [
          { key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'scope', label: 'Scope' }, { key: 'place', label: 'Place' },
          { key: 'isActive', label: 'Active' }, { key: 'usedBy', label: 'Used by utsavs' }, { key: 'createdOn', label: 'Created' }, { key: 'imageUrl', label: 'URL' },
        ], r.rows);
    }
  }
}
